"use client";

import {
  Table,
  VStack,
  Text,
  Button,
  Icon,
  Box,
  HStack,
  Tag,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { LuCheck, LuX } from "react-icons/lu";
import {
  getPricingPlans,
  getFeatureRows,
  type BillingPeriod,
  type PlanKey,
  PricingPlan,
} from "../data/pricingData";
import {
  formatPlanPriceLabel,
  getAnnualBillingDisplayParts,
} from "../_lib/catalogAmounts";
import { CatalogPriceSkeleton } from "./CatalogPriceSkeleton";
import { MdOutlineLocalOffer } from "react-icons/md";
import { BiSolidOffer } from "react-icons/bi";

interface Props {
  billing: BillingPeriod;
  onSelect: (plan: PricingPlan) => void;
  amountByPriceId: Record<string, number>;
  isCatalogLoading: boolean;
}

export default function PricingTable({
  billing,
  onSelect,
  amountByPriceId,
  isCatalogLoading,
}: Props) {
  const t = useTranslations("pricing");
  const pricingPlans = getPricingPlans(t);
  const featureRows = getFeatureRows(t);

  const renderPrice = (plan: PricingPlan) =>
    formatPlanPriceLabel(plan, billing, amountByPriceId, t);

  const renderPeriod = (plan: PricingPlan) => {
    if (plan.isCustom || plan.priceMonthly === 0) return "";
    return billing === "monthly" ? t("common.perMonth") : t("common.perYear");
  };

  const renderPlanPriceColumn = (plan: PricingPlan) => {
    if (isCatalogLoading && (plan.id === "pro" || plan.id === "ultimate")) {
      return <CatalogPriceSkeleton billing={billing} variant="table" />;
    }

    const isPopular = plan.popular;

    const annualParts =
      billing === "annual"
        ? getAnnualBillingDisplayParts(plan, amountByPriceId, t)
        : null;

    if (annualParts) {
      return (
        <VStack gap={1} align="center" h="6rem" py={6}>
          <HStack gap={1} align="baseline">
            <Text
              textStyle="3xl"
              fontWeight="light"
              fontFamily="mono"
              lineHeight={1}
              color={isPopular ? "white" : "neutral.900"}
              letterSpacing="-0.02em"
            >
              {annualParts.headlineAmount}
            </Text>
            <Text
              textStyle="sm"
              fontWeight="medium"
              color={isPopular ? "white" : "neutral.600"}
            >
              {annualParts.headlineSuffix}
            </Text>
          </HStack>
          <Text
            textStyle="xs"
            color={isPopular ? "whiteAlpha.700" : "neutral.500"}
          >
            {annualParts.subline}
          </Text>
        </VStack>
      );
    }

    return (
      <HStack gap={1} align="baseline" justify="center" h="6rem" py={6}>
        <Text
          textStyle="3xl"
          fontWeight="light"
          fontFamily="mono"
          lineHeight={1}
          color={isPopular ? "white" : "gray.800"}
          letterSpacing="-0.02em"
        >
          {renderPrice(plan)}
        </Text>
        <Text
          textStyle="sm"
          fontWeight="medium"
          color={isPopular ? "whiteAlpha.800" : "gray.600"}
          fontFamily="mono"
        >
          {renderPeriod(plan)}
        </Text>
      </HStack>
    );
  };

  const renderValue = (value: string | boolean, isPopular: boolean) => {
    if (typeof value === "boolean") {
      return value ? (
        <Icon
          as={LuCheck}
          color={isPopular ? "white" : "primary.600"}
          boxSize={5}
          aria-hidden
        />
      ) : (
        <Icon
          as={LuX}
          color={isPopular ? "whiteAlpha.700" : "neutral.300"}
          boxSize={5}
          aria-hidden
        />
      );
    }
    let color: string;
    if (value === t("common.unlimited") && isPopular) {
      color = "teal.400";
    } else if (
      value === t("common.unlimited") ||
      value === t("common.custom")
    ) {
      color = "primary.600";
    } else if (isPopular) {
      color = "white";
    } else {
      color = "neutral.600";
    }

    return (
      <Text textStyle="sm" fontWeight="regular" color={color} fontFamily="mono">
        {value}
      </Text>
    );
  };

  const getButtonProps = (plan: PricingPlan) =>
    plan.popular
      ? { colorPalette: "primary" as const, variant: "solid" as const }
      : plan.id === "free" || plan.isCustom
        ? { colorPalette: "neutral" as const, variant: "outline" as const }
        : { colorPalette: "primary" as const, variant: "outline" as const };

  return (
    <Box
      boxShadow="shadow-lg"
      rounded="3xl"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      overflow="hidden"
    >
      <Table.Root variant="line" size="lg">
        <Table.Header>
          <Table.Row bg="gray.50">
            <Table.ColumnHeader
              py={8}
              px={6}
              width="26%"
              borderBottomWidth="0.5px"
              borderColor="neutral.200"
              justifyItems="center"
              fontStyle="italic"
            >
              <HStack gap={2} align="center">
                <Icon
                  as={MdOutlineLocalOffer}
                  boxSize={5}
                  color="neutral.800"
                />
                <Text
                  textStyle="md"
                  fontWeight="regular"
                  color="neutral.800"
                  fontFamily="spaceGrotesk"
                >
                  {t("table.featureColumn")}
                </Text>
              </HStack>
            </Table.ColumnHeader>

            {pricingPlans.map((plan, index) => {
              const isPopular = plan.popular;
              return (
                <Table.ColumnHeader
                  key={plan.id}
                  textAlign="center"
                  py={5}
                  px={4}
                  bg={isPopular ? "dark.900" : "neutral.50"}
                  borderBottomWidth="0.5px"
                  borderColor={isPopular ? "transparent" : "neutral.200"}
                  borderLeftWidth={index > 0 ? "1px" : undefined}
                  borderLeftColor="neutral.100"
                  borderX="1px"
                >
                  <VStack gap={4} align="center">
                    {isPopular ? (
                      <Tag.Root
                        size="xl"
                        colorPalette="teal"
                        variant="solid"
                        rounded="full"
                      >
                        <Tag.Label
                          display="flex"
                          alignItems="center"
                          gap={1}
                          textStyle="xs"
                          fontWeight="bold"
                        >
                          <Icon as={BiSolidOffer} boxSize={5} />
                          {t("common.mostPopular")}
                        </Tag.Label>
                      </Tag.Root>
                    ) : (
                      <Box h="22px" />
                    )}

                    <Text
                      textStyle="lg"
                      fontWeight="semibold"
                      fontFamily="spaceGrotesk"
                      color={isPopular ? "white" : "neutral.900"}
                    >
                      {plan.name}
                    </Text>

                    {renderPlanPriceColumn(plan)}

                    <Button
                      {...getButtonProps(plan)}
                      size="sm"
                      rounded="xl"
                      onClick={() => onSelect(plan)}
                      _hover={{ shadow: "shadow-primary-xl" }}
                      border="none"
                      bg={isPopular ? "white" : "primary.600"}
                      color={isPopular ? "neutral.900" : "white"}
                      shadow={
                        isPopular ? "shadow-primary-lg" : "shadow-primary-md"
                      }
                    >
                      {plan.cta}
                    </Button>
                  </VStack>
                </Table.ColumnHeader>
              );
            })}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {featureRows.map((row, idx) => (
            <Table.Row
              key={row.label}
              bg={idx % 2 === 0 ? "white" : "neutral.50"}
              _hover={{ bg: "primary.50" }}
              transition="background 0.15s ease"
            >
              <Table.Cell
                fontWeight="light"
                py={4}
                px={6}
                textStyle="sm"
                color="neutral.700"
                borderColor="neutral.100"
              >
                {row.label}
              </Table.Cell>

              {pricingPlans.map((plan, planIndex) => (
                <Table.Cell
                  key={plan.id}
                  textAlign="center"
                  py={4}
                  bg={plan.popular ? "dark.900" : undefined}
                  borderLeftWidth={planIndex > 0 ? "0px" : undefined}
                  borderColor="transparent"
                >
                  {renderValue(row.values[plan.id as PlanKey], plan.popular)}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
